import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Plus, X, User, Building2, Phone, Mail, ChevronDown, Users } from 'lucide-react';
import { cn } from '@/lib/format';
import { useCustomers, useSearchCustomers } from '@/hooks/useCustomers';
import type { Customer } from '@/types/domain';

export interface CustomerPickerProps {
  /** Currently selected customer id (controlled). */
  value: string | null;
  /** Called with the picked customer (or null when cleared). */
  onSelect: (customer: Customer | null) => void;
  /** Called when user requests "Add new" inline. */
  onAddNew?: () => void;
  /** Disable interaction. */
  disabled?: boolean;
  /** Placeholder text. */
  placeholder?: string;
  /** Element id for label association. */
  id?: string;
  /** Show as a compact trigger button instead of inline input. */
  asTrigger?: boolean;
  /** Optional className for the root element. */
  className?: string;
}

type PickerItem =
  | { kind: 'customer'; customer: Customer; key: string }
  | { kind: 'add-new'; key: 'add-new' };

/**
 * Search-as-you-type customer picker.
 * - Filters active customers by name/company/phone/email.
 * - Keyboard navigable: ArrowUp/Down to move, Enter to select, Escape to close.
 * - "+ Add new customer" appears at the bottom of the list when a term is typed,
 *   letting the cashier create a customer inline without leaving POS flow.
 */
export function CustomerPicker({
  value,
  onSelect,
  onAddNew,
  disabled = false,
  placeholder = 'Cari customer berdasarkan nama, perusahaan, atau no telp…',
  id,
  asTrigger = false,
  className,
}: CustomerPickerProps) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selected, setSelected] = useState<Customer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { data: results = [], isFetching } = useSearchCustomers(term, {
    activeOnly: true,
    scanLimit: 50,
  });

  // Default list when there's no search term. Shows the most recent
  // active customers so the cashier can pick without having to type
  // first (essential for tablet / walk-in traffic). The list is capped
  // to a small number — anything more should go through search.
  const { data: defaultList = [], isFetching: isFetchingDefaults } = useCustomers(
    { isActive: true, limit: 25 },
  );

  // Compute display items.
  //
  // The "+ Tambah customer" item is ALWAYS shown at the very top of the
  // list (not just when there's a search term). This is intentional for
  // the tablet/POS flow: cashiers often have a walk-in customer with no
  // record yet, and they shouldn't have to type something first just to
  // find the "add new" affordance. The item label adapts:
  //   - With a search term: "Tambah customer 'ani'" (the cashier-typed text)
  //   - Without:           "Tambah customer baru" (generic)
  //
  // The customer list itself is also ALWAYS shown:
  //   - With a search term:  the search results (subset of all customers)
  //   - Without a search term: the 25 most recent active customers
  //     (defaultList) so the cashier can pick without typing first.
  // We pin "add-new" to the top via `items`, then put the actual
  // customer results below it. Active-index math handles the offset so
  // keyboard navigation still works.
  const hasTerm = term.trim().length > 0;
  const showAddNew = !!onAddNew;
  const visibleCustomers = hasTerm ? results : defaultList;
  const items: PickerItem[] = useMemo(
    () => [
      // Add-new first (pinned to the top)
      ...(showAddNew
        ? [{ kind: 'add-new' as const, key: 'add-new' as const }]
        : []),
      // Real customer results below
      ...visibleCustomers.map((c) => ({
        kind: 'customer' as const,
        customer: c,
        key: c.id,
      })),
    ],
    [visibleCustomers, showAddNew],
  );

  // Reset highlight when the item list changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [term, results.length, showAddNew]);

  // Sync the selected customer when value changes externally (e.g. clear from parent).
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected && selected.id === value) return;
    const match = results.find((c) => c.id === value) ?? null;
    setSelected(match);
  }, [value, results, selected]);

  // Click outside to close.
  //
  // On `click`, not `mousedown`. mousedown fires first and unmounted the panel
  // before the click landed; the browser then dispatches the click on the
  // common ancestor of the mousedown and mouseup targets rather than on the
  // button under the cursor, so the button's handler never ran and the first
  // click anywhere outside was silently eaten. Listening for `click` closes
  // the panel AND lets the same click reach what it was aimed at.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  // Scroll the active item into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const commit = (item: PickerItem | null) => {
    if (!item) return;
    if (item.kind === 'add-new') {
      onAddNew?.();
      setOpen(false);
      return;
    }
    setSelected(item.customer);
    onSelect(item.customer);
    setTerm('');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      commit(items[activeIndex] ?? null);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const clear = () => {
    setTerm('');
    setSelected(null);
    onSelect(null);
    inputRef.current?.focus();
  };

  const panel = (
    <div
      data-customer-picker-dropdown
      className={cn(
        'bg-surface border border-line rounded-lg shadow-hover overflow-hidden',
        asTrigger && open && 'border-t-0 rounded-t-none',
      )}
    >
      {isFetching && visibleCustomers.length === 0 && !showAddNew ? (
        <div className="px-4 py-3 text-small text-ink-subtle">
          {hasTerm ? 'Mencari customer…' : 'Memuat daftar customer…'}
        </div>
      ) : visibleCustomers.length === 0 && !showAddNew ? (
        <div className="px-4 py-4 text-small text-ink-subtle text-center">
          {hasTerm ? (
            'Tidak ada customer yang cocok dengan pencarian.'
          ) : isFetchingDefaults ? (
            'Memuat…'
          ) : (
            <>
              <p className="mb-1">Belum ada customer sama sekali.</p>
              <p className="text-caption">Tambah customer pertama dari tombol di atas.</p>
            </>
          )}
        </div>
      ) : (
        <ul
          ref={listRef}
          id="customer-picker-list"
          role="listbox"
          className="max-h-72 overflow-y-auto py-1"
        >
          {!hasTerm && !isFetchingDefaults && defaultList.length > 0 && (
            <li
              className="px-4 pt-2 pb-1.5 flex items-center gap-1.5 text-overline uppercase text-ink-subtle font-medium tracking-wider border-b border-line"
              aria-hidden
            >
              <Users size={11} />
              Customer aktif · terbaru
            </li>
          )}
          {items.map((item, idx) => {
            const isActive = idx === activeIndex;
            if (item.kind === 'add-new') {
              // Adaptive label: with a search term, show the typed
              // name as a preview; without, fall back to a generic
              // "Tambah customer baru". Either way the action is the
              // same: open the new-customer form.
              const label = hasTerm
                ? `Tambah customer "${term.trim()}"`
                : 'Tambah customer baru';
              return (
                <li
                  key="add-new"
                  data-idx={idx}
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(item);
                  }}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2.5 cursor-pointer text-body',
                    'sticky top-0 z-10 bg-surface',
                    'border-b border-line',
                    isActive ? 'bg-primary/5 text-primary' : 'text-ink',
                  )}
                >
                  <span className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0">
                    <Plus size={14} />
                  </span>
                  <span className="font-medium">{label}</span>
                </li>
              );
            }
            const c = item.customer;
            return (
              <li
                key={c.id}
                data-idx={idx}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(item);
                }}
                className={cn(
                  'flex items-start gap-3 px-4 py-2.5 cursor-pointer',
                  isActive ? 'bg-canvas' : 'bg-surface',
                )}
              >
                <span className="w-7 h-7 rounded-full bg-line/40 text-ink-subtle flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-body font-medium text-ink truncate">{c.name}</p>
                  {c.company && (
                    <p className="text-caption text-ink-subtle flex items-center gap-1 truncate">
                      <Building2 size={11} className="flex-shrink-0" />
                      {c.company}
                    </p>
                  )}
                  <p className="text-caption text-ink-subtle flex items-center gap-1">
                    <Phone size={11} className="flex-shrink-0" />
                    {c.phone}
                  </p>
                  {c.email && (
                    <p className="text-caption text-ink-subtle flex items-center gap-1 truncate">
                      <Mail size={11} className="flex-shrink-0" />
                      {c.email}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  // === Trigger mode (compact button) ===
  if (asTrigger) {
    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setOpen((o) => !o);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className={cn(
            'input flex items-center justify-between text-left',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selected ? (
            <span className="flex flex-col min-w-0">
              <span className="text-body font-medium text-ink truncate">{selected.name}</span>
              <span className="text-caption text-ink-subtle truncate">{selected.phone}</span>
            </span>
          ) : (
            <span className="text-neutral">{placeholder}</span>
          )}
          <ChevronDown size={16} className="text-neutral flex-shrink-0" />
        </button>
        {open && (
          <div className="absolute z-30 left-0 right-0 mt-1">
            <input
              ref={inputRef}
              type="text"
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="input mb-1"
            />
            {panel}
          </div>
        )}
      </div>
    );
  }

  // === Inline mode ===
  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Selected customer chip — sits above the input so the selection
          is unambiguous. Clear (X) on the chip removes the selection; the
          input below is always a search field, never re-uses the chip
          text as a placeholder. */}
      {selected && !disabled && (
        <div
          data-customer-picker-chip
          className="mb-1.5 flex items-center gap-2 pl-2 pr-1 py-1 rounded-md bg-primary/10 border border-primary/30"
        >
          <span className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0">
            <User size={14} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-small font-medium text-primary truncate leading-tight">
              {selected.name}
            </p>
            <p className="text-caption text-primary/70 truncate leading-tight">
              {selected.phone}
            </p>
          </div>
          <button
            type="button"
            onClick={clear}
            className="shrink-0 p-1.5 rounded text-primary/70 hover:text-primary hover:bg-primary/15 transition-colors"
            aria-label="Hapus customer"
            title="Hapus customer"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral pointer-events-none">
          <Search size={16} />
        </span>
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="customer-picker-list"
          disabled={disabled}
          value={term}
          placeholder={selected ? 'Cari customer lain…' : placeholder}
          onChange={(e) => {
            setTerm(e.target.value);
            if (selected) {
              setSelected(null);
              onSelect(null);
            }
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className={cn(
            'input pl-10 pr-10',
            selected && 'border-primary/40 bg-primary/5 focus:border-primary',
          )}
        />
        {/* Inline X only shows when there is typed text (not when just
            selected — the chip already has its own X). Prevents redundant
            clear affordances. */}
        {term && !disabled && (
          <button
            type="button"
            onClick={() => {
              setTerm('');
              setOpen(true);
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-neutral hover:text-ink hover:bg-line/30 transition-colors"
            aria-label="Hapus pencarian"
            title="Hapus pencarian"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1">{panel}</div>
      )}
    </div>
  );
}
